'use client'
import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { company } from '@constants/company'

async function trackContact(contactType, propertyId, agentId, saleType, agentName) {
  const { sendGAEvent } = await import('@next/third-parties/google')
  sendGAEvent('event', 'contact_click', {
    contact_type: contactType,
    agent_name:   agentName ?? 'unknown',
    agent_id:   agentId ?? 'unknown',
    property_id:  propertyId ?? 'unknown',
  })
  const { sendClickContact } = await import('@services/sendClickContact')
  sendClickContact(null, propertyId, agentId, saleType, contactType).catch(() => {})
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function r(val, lang = 'en') {
  if (!val) return ''
  if (typeof val === 'string') return val
  return val[lang] ?? val.en ?? Object.values(val)[0] ?? ''
}

// ─── Brand config (from website.brand) — mirrors the editor exactly ─────────
const DEFAULT_BRAND = {
  fontFamily: 'Montserrat',
  backgroundColor: '#ffffff',
  colorMain: '#2563eb',
  colorSecondary: '#f97316',
  thirdColor: '#16a34a',
  fourthColor: '#f8f7f4',
  colorPrice: '#2563eb',
  iconColor: '#16a34a',
  textColor: '#111827',
  radiusMd: '0.375rem',
}
function buildBrandCssVars(brand) {
  return {
    '--color-main':      brand.colorMain,
    '--color-secondary': brand.colorSecondary,
    '--color-third':     brand.thirdColor,
    '--color-fourth':    brand.fourthColor,
    '--color-price':     brand.colorPrice,
    '--icon-color':      brand.iconColor,
    '--text-color':      brand.textColor,
    '--bg-color':        brand.backgroundColor,
    '--radius-md':       brand.radiusMd,
    '--font-family':     `'${brand.fontFamily}', 'Segoe UI', system-ui, sans-serif`,
  }
}

// ─── Agent image URL ──────────────────────────────────────────────────────────
function agentImageUrl(agent) {
  if (!agent?.cover) return null
  if (agent.cover.includes('iList') || agent.cover.includes('welist')) return agent.cover
  return `https://rstbase.com/images/agents/${agent._id}/${agent.cover}`
}

// ─── Build Telegram deep-link ─────────────────────────────────────────────────
function buildTelegramUrl(tg, prefill) {
  if (!tg) return '#'
  if (tg.startsWith('http')) return tg
  const raw = tg.replace(/\s/g, '')
  if (raw.startsWith('+') || /^\d/.test(raw)) {
    return `https://t.me/${raw}`
  }
  const username = raw.replace(/^@/, '')
  const query = prefill ? `?text=${encodeURIComponent(prefill)}` : ''
  return `https://t.me/${username}${query}`
}

// ─── Resolve contact values from agent data ───────────────────────────────────
function resolveContact(btn, agent) {
  const phone     = agent?.agentContact   ?? ''
  const waUrl     = agent?.agentWhatsapp  ?? ''
  const lineUrl   = agent?.agentLine      ?? ''
  const wechat    = agent?.agentWx        ?? ''
  const email     = agent?.agentEmail     ?? ''
  const facebook  = agent?.facebookPage   ?? ''
  const instagram = agent?.instagramPage  ?? ''
  const youtube   = agent?.youtubePage    ?? ''
  const linkedin  = agent?.linkedInPage   ?? ''
  const tiktok    = agent?.agentTiktok    ?? ''
  const telegram  = agent?.agentTelegram  ?? ''

  const lookup = (key) => {
    switch (key) {
      case 'phone':
      case 'mobile':    return { url: `tel:${phone}`,                               displayValue: phone,      hasData: !!phone,      displayLabel: null,        whatsappUrl: null }
      case 'whatsapp':  return { url: waUrl,                                        displayValue: null,       hasData: !!waUrl,      displayLabel: null,        whatsappUrl: waUrl }
      case 'line':      return { url: lineUrl,                                      displayValue: lineUrl,    hasData: !!lineUrl,    displayLabel: 'LINE',      whatsappUrl: null }
      case 'wechat':    return { url: wechat,                                       displayValue: wechat,     hasData: !!wechat,     displayLabel: 'WeChat ID', whatsappUrl: null }
      case 'email':     return { url: `mailto:${email}`,                            displayValue: email,      hasData: !!email,      displayLabel: null,        whatsappUrl: null }
      case 'facebook':  return { url: facebook,                                     displayValue: facebook,   hasData: !!facebook,   displayLabel: null,        whatsappUrl: null }
      case 'instagram': return { url: instagram,                                    displayValue: instagram,  hasData: !!instagram,  displayLabel: null,        whatsappUrl: null }
      case 'youtube':   return { url: youtube,                                      displayValue: youtube,    hasData: !!youtube,    displayLabel: null,        whatsappUrl: null }
      case 'linkedin':  return { url: linkedin,                                     displayValue: linkedin,   hasData: !!linkedin,   displayLabel: null,        whatsappUrl: null }
      case 'tiktok':    return { url: tiktok,                                       displayValue: tiktok,     hasData: !!tiktok,     displayLabel: null,        whatsappUrl: null }
      case 'telegram':  return { url: buildTelegramUrl(telegram, btn.telegramText), displayValue: telegram,   hasData: !!telegram,   displayLabel: 'Telegram',  whatsappUrl: null }
      default:          return { url: btn.url ?? '#',                               displayValue: btn.url,    hasData: !!btn.url,    displayLabel: null,        whatsappUrl: null }
    }
  }

  const iconData = lookup(btn.icon ?? 'url')
  const qrActive = (btn.qrMode ?? 'off') !== 'off'
  const qrData   = qrActive && btn.qrContactType ? lookup(btn.qrContactType) : iconData

  return {
    directUrl:    iconData.url,
    hasData:      iconData.hasData,
    qrUrl:        qrData.url,
    displayValue: qrData.displayValue,
    displayLabel: qrData.displayLabel,
    whatsappUrl:  qrData.whatsappUrl,
  }
}

// ─── Resolve server text by semantic role ─────────────────────────────────────
function resolveText(role, agent, lang) {
  switch (role) {
    case 'name':    return agent?.agentName     ?? ''
    case 'title':   return agent?.position      ?? ''
    case 'company': return agent?.company       ?? company?.name
    case 'address': return agent?.officeAddress ?? ''
    default:        return null
  }
}

// ─── Desktop detection ────────────────────────────────────────────────────────
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const handler = (e) => setIsDesktop(e.matches)
    mq.addEventListener('change', handler)
    setIsDesktop(mq.matches)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isDesktop
}

// ─── Icon presets ─────────────────────────────────────────────────────────────
const ICON_PRESETS = {
  none:      { svg: null },
  phone:     { svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l1.27-.88a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg> },
  mobile:    { svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></svg> },
  email:     { svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg> },
  whatsapp:  { svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg> },
  line:      { svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/></svg> },
  wechat:    { svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c-.276-.94-.418-1.92-.418-2.93 0-3.763 3.417-6.82 7.634-6.82.27 0 .54.014.808.04C15.913 4.187 12.69 2.188 8.69 2.188zm-2.47 3.38a1.016 1.016 0 1 1 0 2.031 1.016 1.016 0 0 1 0-2.031zm4.962 0a1.016 1.016 0 1 1 0 2.031 1.016 1.016 0 0 1 0-2.031zM24 14.465c0-3.32-3.14-6.017-7.02-6.017s-7.021 2.696-7.021 6.017c0 3.32 3.14 6.017 7.02 6.017a8.08 8.08 0 0 0 2.336-.345.69.69 0 0 1 .573.079l1.523.892a.264.264 0 0 0 .135.043.236.236 0 0 0 .232-.236c0-.058-.024-.113-.038-.17l-.312-1.185a.474.474 0 0 1 .17-.533C23.073 18.163 24 16.398 24 14.465zm-9.528-1.016a.813.813 0 1 1 0-1.626.813.813 0 0 1 0 1.626zm4.988 0a.813.813 0 1 1 0-1.626.813.813 0 0 1 0 1.626z"/></svg> },
  facebook:  { svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
  instagram: { svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg> },
  youtube:   { svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> },
  tiktok:    { svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg> },
  twitter:   { svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
  linkedin:  { svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
  website:   { svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><path d="M2 12h20"/></svg> },
  map:       { svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></svg> },
  viber:     { svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.398.002C9.473.028 5.331.344 3.014 2.467 1.13 4.35.484 7.103.413 10.508c-.072 3.406-.158 9.788 5.987 11.526h.005l-.005 2.644s-.04 1.07.664 1.287c.851.262 1.35-.546 2.162-1.423.447-.482.822-.883 1.053-1.148 2.9.245 5.13-.315 5.386-.4.587-.192 3.905-.618 4.443-5.026.557-4.546-.268-7.42-1.73-8.714l-.005-.005c-.422-.388-2.124-1.628-5.445-1.83 0 0-.287-.017-.525-.017zm.064 1.682c.213 0 .43.01.63.018 2.834.172 4.283 1.226 4.628 1.54 1.217 1.075 1.904 3.597 1.43 7.384-.45 3.638-3.058 3.84-3.548 4.003-.207.068-2.12.557-4.607.4-1.16 1.296-1.534 1.713-1.534 1.713v-1.813c-5.23-1.477-4.943-6.974-4.942-7.02C4.606 5.61 5.178 3.432 6.69 1.988c.98-.87 2.7-1.284 4.772-1.304zm-.64 3.303c-.27 0-.54.01-.8.032C7.694 5.19 6.02 6.5 6.02 6.5s-1.45 1.08-1.45 3.4c0 .54.094 1.145.317 1.793.565 1.66 1.71 2.768 3.124 3.374.38.162.734.263 1.038.316v1.074s.008.298.206.357c.243.073.386-.166.619-.44.13-.155.317-.375.5-.6.957.08 1.843-.032 2.61-.258.19-.058.37-.123.54-.194.757-.31 1.406-.843 1.773-1.578.336-.677.423-1.496.21-2.528-.317-1.524-.91-2.37-1.5-2.876-.59-.504-1.25-.826-1.937-1.003a6.54 6.54 0 0 0-1.507-.173zm.095 1.262c.382 0 .787.048 1.195.16.527.14 1.04.396 1.48.773.437.374.878 1.012 1.145 2.296.177.853.106 1.457-.12 1.908-.226.452-.618.794-1.19 1.03-.147.06-.3.117-.464.166-.64.188-1.39.298-2.21.233-.248-.02-.475.048-.653.162-.116.074-.22.17-.306.27l-.198.243v-.584c-.016.005-.03.012-.044.016-.262-.044-.568-.125-.895-.265-1.16-.493-2.028-1.38-2.487-2.71a5.18 5.18 0 0 1-.255-1.519c0-1.84 1.118-2.658 1.118-2.658s1.273-1.007 3.197-1.169a6.28 6.28 0 0 1 .687-.152zm4.977.083c.258 0 .5.122.64.347.223.353.112.818-.248 1.038-.36.22-.83.108-1.052-.245-.222-.353-.11-.818.25-1.038.133-.082.28-.12.41-.102zm-4.854.986c-.14 0-.28.018-.418.056-.622.168-1.14.72-1.35 1.458-.14.484-.096.97.07 1.372.168.402.476.717.866.855.39.138.83.104 1.213-.094.384-.198.667-.538.79-.953.122-.415.07-.855-.148-1.224-.21-.358-.576-.583-.963-.466a.747.747 0 0 0-.06.004zm.205.797c.134-.037.284.044.374.193.086.143.1.325.04.49-.059.165-.19.285-.336.32-.146.036-.296-.04-.385-.186-.09-.146-.103-.33-.041-.498.063-.167.21-.28.348-.32z"/></svg> },
  telegram:  { svg: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg> },
}

// ─── QR image ─────────────────────────────────────────────────────────────────
function QRBox({ url, size = 180 }) {
  const src = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(url || 'https://example.com')}&size=${size}x${size}&margin=8`
  return <img src={src} alt="QR Code" width={size} height={size} style={{ borderRadius: 6, display: 'block' }} />
}

// ─── Copy button ──────────────────────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard?.writeText(text).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button onClick={copy} style={{ fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 5, border: '1px solid', borderColor: copied ? '#86efac' : '#d1d5db', background: copied ? '#f0fdf4' : '#f9fafb', color: copied ? '#16a34a' : '#374151', cursor: 'pointer', transition: 'all 0.2s' }}>
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  )
}

// ─── QR panel content ─────────────────────────────────────────────────────────
function QRPanelContent({ btn, agent }) {
  const { qrUrl, displayValue, displayLabel, whatsappUrl } = resolveContact(btn, agent)
  const ct = btn.qrContactType ?? btn.icon ?? 'url'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%' }}>
      <QRBox url={qrUrl} size={180} />

      {ct === 'phone' && displayValue && <>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827', letterSpacing: '0.03em' }}>{displayValue}</span>
        <CopyButton text={displayValue} />
      </>}

      {ct === 'whatsapp' && whatsappUrl && <>
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 600, padding: '8px 20px', borderRadius: 7, border: '1px solid #86efac', background: '#f0fdf4', color: '#16a34a', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 7 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
          Open in WhatsApp
        </a>
      </>}

      {(ct === 'wechat' || ct === 'line') && displayValue && <>
        {displayLabel && <span style={{ fontSize: 12, color: '#6b7280' }}>{displayLabel}</span>}
        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{displayValue}</span>
        <CopyButton text={displayValue} />
      </>}

      {ct === 'telegram' && displayValue && <>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Telegram</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{displayValue}</span>
        <a href={qrUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 600, padding: '8px 20px', borderRadius: 7, border: '1px solid #a5d8ff', background: '#e7f5ff', color: '#1971c2', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 16, height: 16, display: 'flex' }}>{ICON_PRESETS.telegram.svg}</span>
          Open in Telegram
        </a>
      </>}

      {ct === 'url' && displayValue && <>
        <a href={displayValue} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#2563eb', wordBreak: 'break-all', textAlign: 'center' }}>{displayValue}</a>
      </>}
    </div>
  )
}

// ─── QR modal ─────────────────────────────────────────────────────────────────
function QRModal({ btn, agent, lang, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)' }} />
      <div style={{ position: 'relative', background: '#fff', borderRadius: 16, padding: 28, minWidth: 270, maxWidth: 340, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.22)' }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: '#f3f4f6', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: 15, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 2 }}>{r(btn.label, lang) || 'Contact'}</span>
        <QRPanelContent btn={btn} agent={agent} />
      </div>
    </div>
  )
}

// ─── Placeholder avatar ───────────────────────────────────────────────────────
function AvatarPlaceholder({ size, aspectRatio, radius, borderWidth, borderColor, bgColor, shadow }) {
  return (
    <div style={{ width: size, aspectRatio, borderRadius: radius, border: `${borderWidth}px solid ${borderColor}`, background: bgColor, boxShadow: shadow && shadow !== 'none' ? shadow : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width="40%" height="40%" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
    </div>
  )
}

// ─── Verified badge overlay ───────────────────────────────────────────────────
function Badge({ color }) {
  return (
    <span style={{ position: 'absolute', top: -4, right: -4, width: 18, height: 18, borderRadius: '50%', background: color ?? '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
    </span>
  )
}

// ─── Main renderer ────────────────────────────────────────────────────────────
export default function ContactCardRenderer({ contactCard, agent, propertyId, saleType, lang = 'en', brand, userId }) {
  const [qrOpen,  setQrOpen]  = useState(null)
  const [qrModal, setQrModal] = useState(null)
  const isDesktop = useIsDesktop()
  const toggleInline = (id) => setQrOpen(q => q === id ? null : id)

  // ── Embedded enquiry form state (for a "formfields" row, if present) ───────
  const [formValues, setFormValues]         = useState({})
  const [formSubmitting, setFormSubmitting] = useState(false)
  const [formSubmitted, setFormSubmitted]   = useState(false)
  const setFieldValue = (id, val) => setFormValues(v => ({ ...v, [id]: val }))

  const cfg        = typeof contactCard === 'string' ? JSON.parse(contactCard) : (contactCard ?? {})
  const rows        = cfg.rows       ?? []
  const cardStyle   = cfg.cardStyle  ?? {}
  const fields      = cfg.fields     ?? []
  const formStyle   = cfg.formStyle  ?? {}
  const cs = cardStyle

  // Base route is set per-card in the editor (Card Style tab), defaulting to
  // "agents" -> /agents/{agent-slug}. Falls back to agent._id if no slug field
  // exists on the agent record.
  const linkBase   = (cardStyle.agentLinkBase || 'agents').replace(/^\/+|\/+$/g, '')
  const agentSlug  = agent?.slug ?? agent?._id
  const agentHref  = agentSlug ? `/${linkBase}/${agentSlug}` : null

  const activeBrand   = { ...DEFAULT_BRAND, ...(brand ?? {}) }
  const brandCssVars  = buildBrandCssVars(activeBrand)

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    setFormSubmitting(true)
    try {
      const { sendGAEvent } = await import('@next/third-parties/google')
      sendGAEvent('event', 'lead_enquiry', {
        property_id: propertyId ?? 'unknown',
        agent_id:    agent?._id ?? 'unknown',
      })
      const { sendContactForm } = await import('@services/sendContactForm')
      await sendContactForm({
        ...formValues,
        userId,
        propertyId,
        agentId: agent?._id,
        saleType,
      })
    } catch {
      // Best-effort — still show success to avoid blocking the user on a network blip.
    }
    setFormSubmitting(false)
    setFormSubmitted(true)
  }

  const card = {
    ...brandCssVars,
    background:    cs.bg          ?? '#ffffff',
    border:        `${cs.borderWidth ?? 1}px solid ${cs.borderColor ?? '#e5e7eb'}`,
    borderRadius:  cs.borderRadius ?? 12,
    boxShadow:     cs.shadow       ?? 'none',
    padding:       cs.padding      ?? 24,
    display:       'flex',
    flexDirection: 'column',
    gap:           cs.gap          ?? 14,
    fontFamily:    'var(--font-family, "Segoe UI", system-ui, sans-serif)',
    color:         'var(--text-color, inherit)',
    width:         '100%',
    boxSizing:     'border-box',
  }

  const renderRow = (row) => {
    if (!row.enabled) return null

    // ── Image ───────────────────────────────────────────────────────────────
    if (row.type === 'image') {
      const imgSrc = agentImageUrl(agent)
      const radius = row.shape === 'circle' ? '50%' : row.shape === 'rounded' ? '12px' : '4px'
      const align  = row.align === 'center' ? 'center' : row.align === 'right' ? 'flex-end' : 'flex-start'
      const ar     = row.aspectRatio ?? '1/1'
      const shadow = row.shadow && row.shadow !== 'none' ? row.shadow : 'none'
      const imgContent = (
        <>
          {imgSrc
            ? <img src={imgSrc} alt={agent?.agentName ?? ''} style={{ width: row.size ?? 80, aspectRatio: ar, objectFit: 'cover', borderRadius: radius, border: `${row.borderWidth ?? 0}px solid ${row.borderColor ?? '#e5e7eb'}`, boxShadow: shadow, display: 'block' }} />
            : <AvatarPlaceholder size={row.size ?? 80} aspectRatio={ar} radius={radius} borderWidth={row.borderWidth ?? 0} borderColor={row.borderColor ?? '#e5e7eb'} bgColor={row.bgColor ?? '#f3f4f6'} shadow={shadow} />
          }
          {row.badge && <Badge color={row.badgeColor} />}
        </>
      )
      return (
        <div key={row.id} style={{ display: 'flex', justifyContent: align }}>
          {row.link && agentHref
            ? <Link href={agentHref} style={{ position: 'relative', flexShrink: 0, display: 'block' }}>{imgContent}</Link>
            : <div style={{ position: 'relative', flexShrink: 0 }}>{imgContent}</div>
          }
        </div>
      )
    }

    // ── Text ────────────────────────────────────────────────────────────────
    if (row.type === 'text') {
      const SERVER_ROLES = ['name', 'title', 'company', 'address']
      const content = SERVER_ROLES.includes(row.role)
        ? resolveText(row.role, agent, lang)
        : r(row.text, lang)
      if (!content) return null
      const textStyle = { fontSize: `${row.fontSize ?? 1}rem`, fontWeight: row.fontWeight ?? '400', color: row.color ?? '#374151', textAlign: row.align ?? 'left', fontStyle: row.italic ? 'italic' : 'normal', textDecoration: row.underline ? 'underline' : 'none', overflow: row.truncate ? 'hidden' : 'visible', whiteSpace: row.truncate ? 'nowrap' : 'normal', textOverflow: row.truncate ? 'ellipsis' : 'clip', lineHeight: 1.4 }
      if (row.link && agentHref) {
        return <Link key={row.id} href={agentHref} style={{ ...textStyle, display: 'block' }}>{content}</Link>
      }
      return <div key={row.id} style={textStyle}>{content}</div>
    }

    // ── Divider ─────────────────────────────────────────────────────────────
    if (row.type === 'divider') {
      if (row.label) {
        return (
          <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: `${row.marginY ?? 8}px 0` }}>
            <div style={{ flex: 1, height: row.thickness ?? 1, background: row.color ?? '#e5e7eb' }} />
            <span style={{ fontSize: `${row.labelSize ?? 0.75}rem`, color: row.labelColor ?? '#9ca3af', whiteSpace: 'nowrap' }}>{row.label}</span>
            <div style={{ flex: 1, height: row.thickness ?? 1, background: row.color ?? '#e5e7eb' }} />
          </div>
        )
      }
      return <hr key={row.id} style={{ border: 'none', borderTop: `${row.thickness ?? 1}px ${row.style ?? 'solid'} ${row.color ?? '#e5e7eb'}`, margin: `${row.marginY ?? 8}px 0` }} />
    }

    // ── Profile ─────────────────────────────────────────────────────────────
    if (row.type === 'profile') {
      const imgSrc = agentImageUrl(agent)
      const ar     = row.aspectRatio ?? '1/1'
      const radius = row.shape === 'circle' ? '50%' : row.shape === 'rounded' ? '12px' : '4px'
      const shadow = row.shadow && row.shadow !== 'none' ? row.shadow : 'none'
      const LINE_ROLES = ['name', 'title', 'company', 'address']

      const imgCore = imgSrc
        ? <img src={imgSrc} alt={agent?.agentName ?? ''} style={{ width: row.size ?? 80, aspectRatio: ar, objectFit: 'cover', borderRadius: radius, border: `${row.borderWidth ?? 0}px solid ${row.borderColor ?? '#e5e7eb'}`, boxShadow: shadow, display: 'block' }} />
        : <AvatarPlaceholder size={row.size ?? 80} aspectRatio={ar} radius={radius} borderWidth={row.borderWidth ?? 0} borderColor={row.borderColor ?? '#e5e7eb'} bgColor={row.bgColor ?? '#f3f4f6'} shadow={shadow} />
      const imgEl = (
        <div style={{ position: 'relative', flexShrink: 0 }}>
          {imgCore}
          {row.badge && <Badge color={row.badgeColor} />}
        </div>
      )

      const textBlock = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0, textAlign: row.textAlign ?? 'left' }}>
          {row.lines?.map((line, i) => {
            const content = resolveText(LINE_ROLES[i], agent, lang) ?? r(line.text, lang)
            if (!content) return null
            return (
              <div key={line.id} style={{ fontSize: `${line.fontSize ?? 0.875}rem`, fontWeight: line.fontWeight ?? '400', color: line.color ?? '#374151', fontStyle: line.italic ? 'italic' : 'normal', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {content}
              </div>
            )
          })}
        </div>
      )

      if (row.layout === 'stacked') {
        const stackedStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: row.gap ?? 14 }
        if (row.link && agentHref) return <Link key={row.id} href={agentHref} style={{ ...stackedStyle, textDecoration: 'none', color: 'inherit' }}>{imgEl}{textBlock}</Link>
        return (
          <div key={row.id} style={stackedStyle}>
            {imgEl}{textBlock}
          </div>
        )
      }
      const left  = row.imgSide === 'right' ? textBlock : imgEl
      const right = row.imgSide === 'right' ? imgEl     : textBlock
      const besideStyle = { display: 'flex', flexDirection: 'row', alignItems: row.align ?? 'center', gap: row.gap ?? 14 }
      const besideInner = (
        <>
          <div key="left"  style={{ display: 'flex', flexShrink: 0 }}>{left}</div>
          <div key="right" style={{ flex: 1, minWidth: 0 }}>{right}</div>
        </>
      )
      if (row.link && agentHref) return <Link key={row.id} href={agentHref} style={{ ...besideStyle, textDecoration: 'none', color: 'inherit' }}>{besideInner}</Link>
      return <div key={row.id} style={besideStyle}>{besideInner}</div>
    }

    // ── Embedded form fields (unified module) ──────────────────────────────
    if (row.type === 'formfields') {
      const fs = formStyle
      const showLabels = fs.showFieldLabels ?? true

      if (formSubmitted) {
        const align = fs.successTitleAlign ?? 'center'
        return (
          <div key={row.id} style={{ display: 'flex', flexDirection: 'column', alignItems: align === 'center' ? 'center' : align === 'end' || align === 'right' ? 'flex-end' : 'flex-start', gap: 10, textAlign: align }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <div style={{ fontSize: `${fs.successTitleSize ?? 1.125}rem`, fontWeight: fs.successTitleWeight ?? '700', color: fs.successTitleColor ?? '#111827' }}>
              {r(fs.successTitle, lang) || 'Enquiry Sent!'}
            </div>
            <div style={{ fontSize: `${fs.successSubtitleSize ?? 0.875}rem`, fontWeight: fs.successSubtitleWeight ?? '400', color: fs.successSubtitleColor ?? '#6b7280', lineHeight: 1.5, maxWidth: 300 }}>
              {r(fs.successSubtitle, lang)}
            </div>
          </div>
        )
      }

      const inputStyle = { width: '100%', padding: '8px 10px', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: `${fs.inputFontSize ?? 0.875}rem`, color: fs.inputColor ?? '#111827', background: fs.inputBg ?? '#ffffff', outline: 'none', border: `${fs.inputBorderWidth ?? 1}px solid ${fs.inputBorderColor ?? '#d1d5db'}`, borderRadius: fs.inputRadius ?? 8 }
      const lblStyle   = { display: 'block', fontSize: `${fs.labelSize ?? 0.8125}rem`, fontWeight: fs.labelWeight ?? '500', color: fs.labelColor ?? '#374151', marginBottom: 4 }
      const chevron = (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af' }}>
          <path d="m6 9 6 6 6-6"/>
        </svg>
      )

      return (
        <form key={row.id} onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: cs.gap ?? 14 }}>
          {fs.titleEnabled && (
            <div style={{ fontSize: `${fs.titleSize ?? 1.0625}rem`, fontWeight: fs.titleWeight ?? '700', color: fs.titleColor ?? '#111827', textAlign: fs.titleAlign ?? 'left' }}>
              {r(fs.titleText, lang) || 'Send a Message'}
            </div>
          )}
          {fields.filter(f => f.enabled).map(field => {
            const label = r(field.label, lang)
            const ph    = r(field.placeholder, lang)
            const req   = field.required !== false
            const val   = formValues[field.id] ?? ''

            if (field.type === 'textarea') return (
              <div key={field.id}>
                {showLabels && <label htmlFor={`cc-field-${field.id}`} style={lblStyle}>{label}{req && <span style={{ color: '#ef4444' }}> *</span>}</label>}
                <textarea id={`cc-field-${field.id}`} value={val} onChange={e => setFieldValue(field.id, e.target.value)} placeholder={ph} required={req} rows={field.rows ?? 3} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            )

            if (field.type === 'select') return (
              <div key={field.id}>
                {showLabels && <label htmlFor={`cc-field-${field.id}`} style={lblStyle}>{label}{req && <span style={{ color: '#ef4444' }}> *</span>}</label>}
                <div style={{ position: 'relative' }}>
                  <select id={`cc-field-${field.id}`} value={val} onChange={e => setFieldValue(field.id, e.target.value)} required={req} style={{ ...inputStyle, appearance: 'none', cursor: 'pointer', paddingRight: 28 }}>
                    <option value="" disabled>{ph || 'Select'}</option>
                    {(field.options ?? []).map((opt, oi) => <option key={oi} value={opt}>{opt}</option>)}
                  </select>
                  {chevron}
                </div>
              </div>
            )

            return (
              <div key={field.id}>
                {showLabels && <label htmlFor={`cc-field-${field.id}`} style={lblStyle}>{label}{req && <span style={{ color: '#ef4444' }}> *</span>}</label>}
                <input id={`cc-field-${field.id}`} type={field.type === 'email' ? 'email' : 'text'} value={val} onChange={e => setFieldValue(field.id, e.target.value)} placeholder={ph} required={req} style={inputStyle} />
              </div>
            )
          })}
          {fs.termsEnabled && (
            <div style={{ fontSize: '0.8125rem', color: '#111827', lineHeight: 1.5 }}>
              {r(fs.termsText, lang) || 'By submitting this form I agree to'}{' '}
              <span style={{ color: fs.termsLinkColor ?? '#2563eb' }}>{r(fs.termsLinkText, lang) || 'Terms of Use'}</span>
            </div>
          )}
          {(fs.showSubmitButton ?? true) && (
            <button type="submit" disabled={formSubmitting}
              style={{ width: fs.btnFullWidth ?? true ? '100%' : 'auto', padding: '10px 18px', fontFamily: 'inherit', fontSize: `${fs.btnFontSize ?? 0.9375}rem`, fontWeight: fs.btnFontWeight ?? '700', color: fs.btnColor ?? '#ffffff', background: formSubmitting ? `${fs.btnBg ?? '#2563eb'}99` : (fs.btnBg ?? '#2563eb'), border: 'none', borderRadius: fs.btnRadius ?? 8, cursor: formSubmitting ? 'not-allowed' : 'pointer', transition: 'opacity 0.2s' }}>
              {formSubmitting ? '…' : (r(fs.btnText, lang) || 'Send Message')}
            </button>
          )}
        </form>
      )
    }

    // ── Buttons ─────────────────────────────────────────────────────────────
    if (row.type === 'buttons') {
      const layout    = row.layout    ?? 'stack'
      const gap       = row.gap       ?? 8
      const iconOnly  = row.iconOnly  ?? false
      const gbs       = row.btnStyle  ?? 'solid'
      const gSize     = row.size      ?? 'md'
      const gRad      = row.radius    ?? 8
      const gFull     = row.fullWidth ?? true
      const gIcon     = row.showIcon  ?? true
      const gLbl      = row.showLabel ?? true
      const tileSize  = row.iconSize      ?? 44
      const iconInner = row.iconInnerSize ?? 20
      const iconShape = row.iconShape     ?? 'rounded'
      const tileRad   = iconShape === 'circle' ? 9999 : iconShape === 'rounded' ? Math.round(tileSize * 0.22) : 4
      const align     = row.align ?? 'left'
      const cssAlign  = align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : align
      const rowShadow = row.shadow && row.shadow !== 'none' ? row.shadow : 'none'

      const containerStyle =
        layout === 'stack' ? { display: 'flex', flexDirection: 'column', gap, alignItems: cssAlign } :
        layout === 'grid2' ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap } :
                             { display: 'flex', flexDirection: 'row', gap, flexWrap: 'wrap', justifyContent: cssAlign }

      const inlineQrBtn = qrOpen ? row.buttons?.find(b => b.id === qrOpen && (b.qrMode ?? 'off') === 'inline') : null

      return (
        <div key={row.id}>
          <div style={containerStyle}>
            {row.buttons?.map(btn => {
              const { directUrl, hasData } = resolveContact(btn, agent)
              if (!hasData) return null
              const qrMode = btn.qrMode ?? 'off'
              // Per-button style override — falls back to the row's shared style.
              const bs = (btn.styleOverride && btn.styleOverride !== 'inherit') ? btn.styleOverride : gbs

              // ── Icon-only tile ────────────────────────────────────────────
              if (iconOnly) {
                const iconSvg = ICON_PRESETS[btn.icon ?? 'none']?.svg
                let bg = btn.bg ?? '#2563eb', color = btn.color ?? '#fff', border = 'none'
                if (bs === 'outline')                      { bg = 'transparent'; color = btn.bg ?? '#2563eb'; border = `2px solid ${btn.borderColor ?? btn.bg ?? '#2563eb'}` }
                else if (bs === 'ghost' || bs === 'text')  { bg = 'transparent'; color = btn.bg ?? '#2563eb'; border = 'none' }
                else                                        { bg = btn.bg ?? '#2563eb'; color = btn.color ?? '#fff'; border = `1px solid ${btn.borderColor ?? btn.bg ?? '#2563eb'}` }
                const tileStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: tileSize, height: tileSize, borderRadius: tileRad, background: bg, border, boxShadow: rowShadow, cursor: 'pointer', flexShrink: 0, textDecoration: 'none', transition: 'opacity 0.15s' }
                const iconEl = iconSvg ? <span style={{ width: iconInner, height: iconInner, display: 'flex', color, flexShrink: 0 }}>{iconSvg}</span> : null

                if (qrMode !== 'off') {
                  if (!isDesktop) {
                    return (
                      <a key={btn.id} href={directUrl || '#'} target="_blank" rel="noopener noreferrer" style={tileStyle}
                        onClick={() => trackContact(btn.icon, propertyId, agent?._id, saleType, agent?.agentName)}
                      >{iconEl}</a>
                    )
                  }
                  const handleClick = () => {
                    trackContact(btn.icon, propertyId, agent?._id, saleType, agent?.agentName)
                    qrMode === 'modal' ? setQrModal(btn) : toggleInline(btn.id)
                  }
                  return <button key={btn.id} onClick={handleClick} style={tileStyle}>{iconEl}</button>
                }
                return (
                  <a key={btn.id} href={directUrl || '#'} target="_blank" rel="noopener noreferrer" style={tileStyle}
                    onClick={e => {
                      if (!directUrl || directUrl === '#') { e.preventDefault(); return }
                      trackContact(btn.icon, propertyId, agent?._id, saleType, agent?.agentName)
                    }}
                  >{iconEl}</a>
                )
              }

              // ── Labelled button ───────────────────────────────────────────
              const sizes     = { sm: '8px 12px',  md: '10px 16px', lg: '13px 20px' }
              const fontSizes = { sm: '0.8125rem', md: '0.875rem',  lg: '1rem' }
              const pad = sizes[gSize], fs = fontSizes[gSize]
              let bg = '#2563eb', color = '#fff', border = 'none'
              if      (bs === 'solid')   { bg = btn.bg ?? '#2563eb'; color = btn.color ?? '#fff'; border = `1px solid ${btn.borderColor ?? btn.bg ?? '#2563eb'}` }
              else if (bs === 'outline') { bg = 'transparent'; color = btn.bg ?? '#2563eb'; border = `1px solid ${btn.borderColor ?? btn.bg ?? '#2563eb'}` }
              else if (bs === 'ghost')   { bg = 'transparent'; color = btn.bg ?? '#2563eb'; border = 'none' }
              else if (bs === 'pill')    { bg = btn.bg ?? '#2563eb'; color = btn.color ?? '#fff'; border = `1px solid ${btn.borderColor ?? btn.bg ?? '#2563eb'}` }
              else if (bs === 'text')    { bg = 'transparent'; color = btn.bg ?? '#2563eb'; border = 'none' }
              const radius  = bs === 'pill' ? 9999 : gRad
              const iconEl  = gIcon && ICON_PRESETS[btn.icon ?? 'none']?.svg ? <span style={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{ICON_PRESETS[btn.icon].svg}</span> : null
              const labelEl = gLbl && r(btn.label, lang) ? <span>{r(btn.label, lang)}</span> : null
              const btnStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: pad, fontSize: fs, fontWeight: '600', color, background: bg, border, borderRadius: radius, boxShadow: rowShadow, cursor: 'pointer', width: gFull ? '100%' : 'auto', fontFamily: 'inherit', transition: 'opacity 0.15s', textDecoration: 'none', boxSizing: 'border-box' }

              if (qrMode !== 'off') {
                if (!isDesktop) {
                  return (
                    <a key={btn.id} href={directUrl || '#'} target="_blank" rel="noopener noreferrer" style={btnStyle}
                      onClick={() => trackContact(btn.icon, propertyId, agent?._id, saleType, agent?.agentName)}
                    >{iconEl}{labelEl}</a>
                  )
                }
                const handleClick = () => {
                  trackContact(btn.icon, propertyId, agent?._id, saleType, agent?.agentName)
                  qrMode === 'modal' ? setQrModal(btn) : toggleInline(btn.id)
                }
                return <button key={btn.id} onClick={handleClick} style={btnStyle}>{iconEl}{labelEl}</button>
              }
              return (
                <a key={btn.id} href={directUrl || '#'} target="_blank" rel="noopener noreferrer" style={btnStyle}
                  onClick={e => {
                    if (!directUrl || directUrl === '#') { e.preventDefault(); return }
                    trackContact(btn.icon, propertyId, agent?._id, saleType, agent?.agentName)
                  }}
                >{iconEl}{labelEl}</a>
              )
            })}
          </div>

          {inlineQrBtn && (
            <div style={{ marginTop: 8, padding: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.09)' }}>
              <QRPanelContent btn={inlineQrBtn} agent={agent} />
              <button onClick={() => setQrOpen(null)} style={{ fontSize: 11, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', display: 'block', margin: '10px auto 0' }}>close</button>
            </div>
          )}
        </div>
      )
    }

    return null
  }

  return (
    <div style={{ position: 'relative' }}>
      {qrModal && <QRModal btn={qrModal} agent={agent} lang={lang} onClose={() => setQrModal(null)} />}
      <div style={card}>{rows.map(renderRow)}</div>
    </div>
  )
}